import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AuthenticationService, AppConfigService } from '@alfresco/adf-core';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

/**
 * S6 / Strike 2e — frontend client for the per-instance filing record AND
 * the folio sequence allocator.
 *
 * <p>Two endpoint families:
 * <ul>
 *   <li>{@code /api/wf/instances/{id}/filing} — record / read the filing
 *       result (one row per instance).</li>
 *   <li>{@code /api/wf/folio-seq/{nodeId}/allocate} — atomically allocate
 *       the next folio number for an Alfresco file folder.</li>
 * </ul>
 */
@Injectable({ providedIn: 'root' })
export class WfInstanceFilingApiService {

  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthenticationService);
  private readonly appConfig = inject(AppConfigService);

  private readonly base: string;

  constructor() {
    const apiUrl = this.appConfig.get<string>('skyplus.apiUrl', '/bpm/api');
    this.base = `${apiUrl}/wf`;
  }

  /**
   * Read the existing filing for an instance. Resolves to {@code null} when
   * the instance hasn't been filed yet (server returns 404).
   */
  get(instanceId: number): Observable<InstanceFiling | null> {
    return this.http.get<InstanceFiling>(
      `${this.base}/instances/${instanceId}/filing`,
      { headers: authHeaders(this.auth) }
    ).pipe(
      catchError(err => err?.status === 404 ? of(null) : throwError(err))
    );
  }

  /** Record a new filing — frontend has already allocated folio + created folder. */
  record(instanceId: number, req: RecordFilingRequest): Observable<InstanceFiling> {
    return this.http.post<InstanceFiling>(
      `${this.base}/instances/${instanceId}/filing`,
      req,
      { headers: authHeadersJson(this.auth) }
    );
  }

  /**
   * All filings under the same Alfresco file folder (e.g. staff folder
   * PF009501), ordered by folio number — the siblings shown in the folio rail.
   * Resolves to an empty array on error.
   */
  listByFileNode(fileNodeId: string): Observable<InstanceFiling[]> {
    return this.http.get<InstanceFiling[]>(
      `${this.base}/filings/by-file-node`,
      { headers: authHeaders(this.auth), params: { fileNodeId } }
    ).pipe(
      catchError(() => of([] as InstanceFiling[]))
    );
  }

  /**
   * Atomically allocate the next folio number for an Alfresco file folder.
   * First call returns 1, subsequent calls increment.
   */
  allocateFolio(alfrescoFileNodeId: string): Observable<FolioAllocation> {
    return this.http.post<FolioAllocation>(
      `${this.base}/folio-seq/${encodeURIComponent(alfrescoFileNodeId)}/allocate`,
      {},
      { headers: authHeadersJson(this.auth) }
    );
  }

  /**
   * Phase 5 — move an existing filing to a different file. Role-gated on the
   * server (FILING_MOVE / workflow-admin). The caller must have already, in the
   * destination file: allocated the next folio, created the folio folder, and
   * moved the document node. Records the move + writes the permanent audit.
   */
  move(instanceId: number, req: MoveFilingRequest): Observable<InstanceFiling> {
    return this.http.post<InstanceFiling>(
      `${this.base}/instances/${instanceId}/filing/move`,
      req,
      { headers: authHeadersJson(this.auth) }
    );
  }

  /** Phase 5 — move history for an instance (newest first). */
  moveAudit(instanceId: number): Observable<FilingMoveAudit[]> {
    return this.http.get<FilingMoveAudit[]>(
      `${this.base}/instances/${instanceId}/filing/move-audit`,
      { headers: authHeaders(this.auth) }
    ).pipe(catchError(() => of([] as FilingMoveAudit[])));
  }
}

function authHeaders(auth: AuthenticationService): HttpHeaders {
  const token = auth.getToken();
  return token
    ? new HttpHeaders({ 'Authorization': `Bearer ${token}` })
    : new HttpHeaders();
}

function authHeadersJson(auth: AuthenticationService): HttpHeaders {
  return authHeaders(auth).set('Content-Type', 'application/json');
}

function throwError(err: any): Observable<never> {
  return new Observable<never>(sub => sub.error(err));
}

// ────────────────────────────────────────────────────────────────────────
// Types — mirror the backend DTOs
// ────────────────────────────────────────────────────────────────────────

export interface InstanceFiling {
  id: number;
  wfInstanceId: number;
  fileNodeId: string;
  fileFolderName?: string;
  filePath?: string;
  folioNodeId: string;
  folioNumber: number;
  folioFolderName: string;
  mailRef?: string;
  mailType?: string;
  subject?: string;
  fromOrg?: string;
  dateReceived?: string;
  priority?: string;
  siteId?: string;
  filedBy?: string;
  filedAt?: string;
  fileReference?: string;   // RF-4 — e.g. EACC.6/12/2(1); present when references are on
}

export interface RecordFilingRequest {
  fileNodeId: string;
  fileFolderName?: string;
  filePath?: string;
  folioNodeId: string;
  folioNumber: number;
  folioFolderName: string;
  mailRef?: string;
  mailType?: string;
  subject?: string;
  fromOrg?: string;
  dateReceived?: string;
  priority?: string;
  siteId?: string;
}

export interface FolioAllocation {
  alfrescoNodeId: string;
  nextSeq: number;
}

/** Phase 5 — body to move an existing filing into a different file. */
export interface MoveFilingRequest {
  newFileNodeId: string;
  newFileFolderName?: string;
  newFilePath?: string;
  newFolioNodeId: string;
  newFolioNumber: number;
  newFolioFolderName: string;
  reason: string;
}

/** Phase 5 — one entry in a filing's move history. */
export interface FilingMoveAudit {
  id: number;
  wfInstanceId: number;
  filingId?: number;
  oldFileNodeId: string;
  oldFileFolderName?: string;
  oldFilePath?: string;
  oldFolioNodeId?: string;
  oldFolioNumber?: number;
  oldFolioFolderName?: string;
  newFileNodeId: string;
  newFileFolderName?: string;
  newFilePath?: string;
  newFolioNodeId?: string;
  newFolioNumber?: number;
  newFolioFolderName?: string;
  reason: string;
  movedBy?: string;
  movedAt?: string;
}